import { Iterable, Map } from 'immutable';
import { find, forEach, map, some } from 'lodash';
import React, { createContext, useEffect, useMemo, useReducer } from 'react';
import enums from '../enum';
import componentServices from 'Scripts/componentServices';
import * as employeeActions from 'Scripts/employeeActions';
import * as jobActions from 'Scripts/jobActions';
import { getInspectionConfig } from 'Scripts/actions';
import store, { getCollection, ModelCollection } from 'Scripts/store';
import { IEmployee } from 'Scripts/models/employeeModel';
import { IJob } from 'Scripts/models/jobModel';
import siteVisitFactory, { ISiteVisit, SiteVisitType } from 'Scripts/models/siteVisitModel';
import { IJobCost } from 'Scripts/models/jobCostModel';
import { SiteVisitEmployeeRole } from 'Scripts/models/siteVisitEmployeeModel';
import modelSet, { IModelSet } from 'Scripts/modelSet';
import jobIssueFactory, { IJobIssue } from 'Scripts/models/jobIssueModel';
import { IInspectionChecklistItem } from 'Scripts/models/inspectionChecklistItemModel';
import { IInspectionModelFacade, inspectionModelFacadeFactory } from 'Scripts/models/inspectionModelFacade';

export enum ContractPhase {
	unknown = 'Unknown',
	inspect = 'Inspect',
	install = 'Install',
	deliver = 'Deliver',
	service = 'Service'
}

export interface IFieldAppQueueItem {
	jobKey: string;
	jobCostKey: string;
	name: string;
	relayNumber: string;
	accountingId: string;
	clientContactName: string;
	address: string;
	fieldSupervisorName: string;
	fieldSupervisorKey: string;
	installerNames: string[];
	orderDate?: Date;
	builderJobNumber: string;
	currentStatus: string;
	companyVariant: string;
	isPinned?: boolean;
	phase: ContractPhase;
}

export interface IFieldAppState {
	// configs: Record<string, any>;
	isFetchingJobs?: boolean;
	queueItems: IFieldAppQueueItem[];
	activeInspections: IModelSet<IInspectionModelFacade>;
	pinnedJobCostKeys: string[];
	// contracts: IModelSet<IJobCost>;
	// employees: IModelSet<IEmployee>;
	// jobs: IModelSet<IJob>;
}

interface IFieldActions {
	fetchJobs(): Promise<void>;
	setInspectionData(data: IInspectionModelFacade): Promise<void>;
}

interface IFieldAppContextState {
    fieldState: IFieldAppState;
	fieldActions: IFieldActions
}

const NO_OP_ASYNC = () => Promise.resolve();
const NO_OP = () => undefined;
const NO_OP_FIELD_ACTIONS: IFieldActions = {
	fetchJobs: NO_OP_ASYNC,
	setInspectionData: NO_OP
}
const FIELD_STATUSES = ['order-processed', 'order-delivered', 'installed', 'inspected'];

const reducers = {
	beginFetchJobs(state: IFieldAppState) {
		let result = state;

		if(!state.isFetchingJobs) {
			result = {...state, isFetchingJobs: true };
		}

		return result;
	},
	endFetchJobs(state: IFieldAppState, payload) {
		const result = { ...state, isFetchingJobs: false };

		if(!payload?.error) {
			// get items grouped by contract that includes the jobcost and related visits
			onFetchJobs(result);

			// TODO: retain pinned items
			// result.queueItems = queueItems; // state.queueItems.filter(i => i.isPinned);
		}

		return result;
	},
	beginSetInspectionData(state: IFieldAppState, payload: IInspectionModelFacade) {
		const result = {...state};

		result.activeInspections = modelSet(result.activeInspections.toArray(), inspectionModelFacadeFactory);
		result.activeInspections.set(payload.visit.key, payload);
		result.activeInspections[payload.visit.key].incrementVersion();

		return result;
	},
	endSetInspectionData(state: IFieldAppState, payload: IInspectionModelFacade) {
		const result = {...state};

		result.activeInspections = modelSet(result.activeInspections.toArray(), inspectionModelFacadeFactory);
		result.activeInspections.set(payload.visit.key, payload);
		result.activeInspections[payload.visit.key].incrementVersion();
		
		return result;
	}
}

function fieldAppReducer(state: IFieldAppState, action: { type: keyof typeof reducers, payload?: any}): IFieldAppState {
	let result = state;
	const reducer = reducers[`${action.type}`];

	if(typeof reducer === 'function') {
		result = reducer(result, action.payload);
	}

	return result;
}

const _initialFieldAppState: IFieldAppState = {
	// configs: {},
	isFetchingJobs: false,
	queueItems: [],
	activeInspections: modelSet<IInspectionModelFacade>([], inspectionModelFacadeFactory),
	pinnedJobCostKeys: []
	// contracts: modelSet<IJobCost>([], jobCostFactory),
	// employees: modelSet<IEmployee>([], employeeFactory),
	// jobs: modelSet<IJob>([], jobFactory),
	// siteVisits: modelSet<ISiteVisit>([], siteVisitFactory)
}
export const FieldAppContext = createContext<IFieldAppContextState>({ fieldState: _initialFieldAppState,  fieldActions: NO_OP_FIELD_ACTIONS });

export default ({ children }: { children?: React.ReactNode}) => {
	const {securityContext} = componentServices;
	const currentUser = securityContext.getCurrentUser();
	const [fieldState, dispatch] = useReducer(fieldAppReducer, _initialFieldAppState);

	const fieldActions: IFieldActions =  useMemo(() => {
		return {
			async fetchJobs() {
				dispatch({ type: 'beginFetchJobs' });
				
				try {
					// greedy caching data for offline use - on bootsrap there should already be a request in flight
					await employeeActions.fetchEmployees();
					await jobActions.fetchActiveJobs({ moduleKey: 'inspection'});
					await jobActions.fetchActiveJobs({ moduleKey: 'service'});

					const storeInstance = store();
					const fetchPromises:Promise<any>[] = []; // any side-effect fetches necessary (i.e. inspection configs, etc.)
					const jobCosts = getCollection({store: storeInstance, collection: ModelCollection.jobCosts });

					jobCosts.forEach(jc => {
						const currentStatus = jc.currentStatusItem?.status;
						const changeOrders = jc.changeOrders.toArray();
				
						if(FIELD_STATUSES.indexOf(currentStatus) !== -1 || some(changeOrders, co => FIELD_STATUSES.indexOf(co.currentStatusItem?.status) !== -1)) {
							const { fetchPromise } = getInspectionConfig(jc);

							if(fetchPromise) {
								fetchPromises.push(fetchPromise);
							}
						}
					});

					await Promise.all(fetchPromises);
				} 
				catch(err) {
					dispatch({type: 'endFetchJobs', payload: { error: err }});
				}
				dispatch({type: 'endFetchJobs'});
			}, 
			async setInspectionData(data: IInspectionModelFacade) {
				dispatch({ type: 'beginSetInspectionData', payload: data });

				// TODO: write this to IndexedDB (and handle failure?)

				dispatch({ type: 'endSetInspectionData', payload: data });

				return Promise.resolve();
			}
		}
	}, [dispatch]);

	useEffect(() => {
		if(currentUser) {
			fieldActions.fetchJobs();
		}
	}, [currentUser]);

	return <FieldAppContext.Provider value={ { fieldState, fieldActions }}>{ children }</FieldAppContext.Provider>
};


function onFetchJobs(currentState: Omit<IFieldAppState, 'isFetchingJobs'>) {
	// TODO: merge unsubmitted visits and pinned jobcosts
	const { store } = componentServices;
	const employees = getCollection({ store, collection: ModelCollection.employees });
	const jobs = getCollection({ store, collection: ModelCollection.jobs });
	const jobCosts = getCollection({ store, collection: ModelCollection.jobCosts });
	const siteVisits = getCollection({ store, collection: ModelCollection.siteVisits });
	const configs = {};
	// const contracts: IJobCost[] = [];
	const queueItems:IFieldAppQueueItem[] = [];

	jobCosts.forEach(jc => {
		const currentStatus = jc.currentStatusItem?.status;
		const changeOrders = jc.changeOrders.toArray();

		if(FIELD_STATUSES.indexOf(currentStatus) !== -1 || some(changeOrders, co => FIELD_STATUSES.indexOf(co.currentStatusItem?.status) !== -1)) {
			queueItems.push(mapQueueItem({ employees, jobs, contract: jc, siteVisits, currentStatus }));
			// contracts.push(jc);

			const config = getInspectionConfig(jc);

			configs[config.inspectionConfigName] = config.inspectionConfig;
		}
	});

	// NOTE: Checklist consists of pre-configured checklist definition and contract specifications (should the checklist definition allow for configuration of inspectable spec keys?)
	// A checklist item can be submitted only once for a contract
	// checklist item values are stored with the visit (not the contract) to better retain a history (currently they are a child of the JobCost)
	// the contract should store a checklist definition version as it does with the install config version - this should be set on the first inspection visit submission

	// DONE: fetch inspection checklist definitions (latest and any older definitions based on fetched contracts)
	// TODO: fetch products names/skus from each contract order (Accumatica integration)
	// TODO: add a service worker to the field app to precache app assets for offline use, use a network-first strategy for api requests, and cache first for file assets
	// TODO: caching currentState in IndexedDB and be sure failed api request don't overwrite in-progress data

	currentState.queueItems = queueItems;
}

function mapQueueItem({ employees, jobs, contract, siteVisits, currentStatus }: 
	{ employees: Map<string, IEmployee>, jobs: Map<string, IJob>, contract: IJobCost, siteVisits: Map<string, ISiteVisit>, currentStatus: string }): IFieldAppQueueItem {
	// TODO: set the ever-changing, ever-elusive "phase"
	const job = jobs.get(contract.jobKey);
	const fieldSupervisorKey = job.fieldSupervisorKey;
	const fieldSupervisor: IEmployee = employees.find(e => e.key === fieldSupervisorKey);
	const orderDate: Date = contract.getMostRecentStatusByStatus('order-processed')?.dateCreated;
	const inProgressVisits = siteVisits.toArray().filter(v => !v.completedDate)
	const installVisits = siteVisits.toArray().filter(v => v.jobKey === job.key && v.type === SiteVisitType.install && some(v.jobCosts, jc => jc.jobCostKey === contract.key));
	const installerKeys = map(installVisits, v => find(v.employees, e => e.role === SiteVisitEmployeeRole.primary)?.employeeKey);
	const installers = employees.filter(e => installerKeys.indexOf(e.key) !== -1);
	let phase = ContractPhase.unknown;


	if(inProgressVisits.some(v => v.type === SiteVisitType.inspection)){
		phase = ContractPhase.inspect;
	}
	else if(inProgressVisits.some(v => v.type === SiteVisitType.install)) {
		phase = ContractPhase.install;
	}
	else if(inProgressVisits.some(v => v.type === SiteVisitType.delivery)) {
		phase = ContractPhase.deliver;
	}
	else if(inProgressVisits.some(v => v.type === SiteVisitType.service)) {
		phase = ContractPhase.service;
	}

	return {
		jobKey: contract.jobKey,
		jobCostKey: contract.key,
		relayNumber: contract.relayNumber,
		accountingId: contract.accountingId??'-',
		name: contract.name,
		clientContactName: job.primaryClientContactName?? '-',
		address: job.workSiteAddress?.street??'-',
		fieldSupervisorName: fieldSupervisor?.fullName??'-',
		fieldSupervisorKey,
		orderDate,
		builderJobNumber: job.builderJobNumber??'-',
		currentStatus,
		companyVariant: contract.companyVariant,
		installerNames: installers.toArray().map(i => i.fullName),
		phase
	};
}